import { Check, Plus, RotateCcw, Save, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../services/api";
import { formatFullDateTime, safeText } from "../../lib/formatters";
import { reviewFlagLabel, riskFlagLabel, SEVEN_STEP_VERSION, VERIFICATION_LEVEL_LABELS } from "../../lib/research";
import { sanitizeEmailHtml } from "../../lib/sanitize";
import { Button } from "../../components/ui/Button";
import { Badge, Stars } from "../../components/ui/Badge";
import { ErrorState, LoadingState } from "../../components/ui/Feedback";
import { Field, SelectInput, TextArea, TextInput } from "../../components/ui/Form";
import { ConfirmDialog, Modal } from "../../components/ui/Overlay";

const RATING_DIMENSIONS = {
  product: {
    label: "来源产品",
    rules: [
      "0分：来源产品为空，或没有可识别的产品/规模信息",
      "1分：小于 10 的 kW/kWh 级规模",
      "3分：10–29.99 的 kW/kWh 级规模；或仅有产品/组件数量",
      "5分：30–200 的 kW/kWh 级规模",
      "7分：大于 200 的 kW/kWh 级规模",
    ],
  },
  identity: {
    label: "姓名 / 公司",
    rules: ["0分：姓名/公司缺失或明显无效", "2分：存在可用的客户姓名或公司名称"],
  },
  email: {
    label: "邮箱",
    rules: [
      "0分：缺失、格式无效或测试/临时邮箱",
      "2分：有效公共邮箱，尚无公开身份匹配",
      "5分：有效企业邮箱；或公共邮箱已公开匹配",
      "10分：企业域名包含新能源行业关键词",
    ],
  },
  phone: {
    label: "电话 / WhatsApp",
    rules: [
      "0分：缺失、长度无效或明显重复号码",
      "5分：存在 6–15 位有效号码",
      "10分：有效号码且命中特征尾号规则",
    ],
  },
  message: {
    label: "邮件需求",
    rules: [
      "0分：正文为空、乱码或非文字占比过高",
      "3分：只有一般询价/目录/联系请求",
      "8分：明确提到产品、规模或与来源产品一致",
      "15分：产品明确，并包含至少两类项目细节",
      "18分：满足 15 分，且明确指向 BLUESUN/BSM 型号",
    ],
  },
};

const RATING_STAR_THRESHOLDS = [
  ["1星", "0–8分"],
  ["2星", "9–16分"],
  ["3星", "17–25分"],
  ["4星", "26–36分"],
  ["5星", "37–47分"],
];

const MESSAGE_DETAIL_LABELS = {
  quantity: "数量",
  capacity: "容量/功率",
  voltage: "电压",
  phase: "相制",
  application: "应用场景",
  shipping: "交付/贸易条款",
};

const DOMAIN_TYPE_LABELS = {
  public: "公共邮箱",
  enterprise: "企业邮箱",
  industry_enterprise: "新能源行业企业邮箱",
};

function shortEvidence(value, maximum = 300) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length > maximum ? `${text.slice(0, maximum)}…` : text;
}

function ratingEvidenceLines(key, item, inquiry) {
  const features = item?.features || {};
  const lines = [];
  if (key === "product") {
    lines.push(`来源产品：${features.source_product || inquiry.source_product_raw || inquiry.product || "未填写"}`);
    if (Number(features.normalized_scale || 0) > 0) lines.push(`识别到的规模值：${features.normalized_scale}`);
    if (Number(features.component_count || 0) > 0) lines.push(`识别到的组件数量：${features.component_count}`);
  } else if (key === "identity") {
    lines.push(`采用的姓名/公司：${features.identity || inquiry.company_name || inquiry.customer_name || inquiry.sender_name || "未填写"}`);
  } else if (key === "email") {
    lines.push(`客户邮箱：${features.email || inquiry.sender_email || "未填写"}`);
    if (features.domain_type) lines.push(`邮箱类型：${DOMAIN_TYPE_LABELS[features.domain_type] || features.domain_type}`);
    if ((features.industry_keywords || []).length) lines.push(`命中行业词：${features.industry_keywords.join("、")}`);
    if (features.verification_status) lines.push(`公开核验：${CONTACT_STATUS_LABELS[features.verification_status] || features.verification_status}`);
  } else if (key === "phone") {
    lines.push(`客户号码：${inquiry.phone || features.digits || "未填写"}`);
    if ((features.patterns || []).length) lines.push(`命中特征：${features.patterns.join("、")}`);
  } else if (key === "message") {
    const detailEvidence = features.detail_evidence || {};
    const details = (features.details || []).map((name) => {
      const evidence = detailEvidence[name];
      return `${MESSAGE_DETAIL_LABELS[name] || name}${evidence ? `（原文：${evidence}）` : ""}`;
    });
    if (features.product_evidence) lines.push(`产品/规模命中：原文“${features.product_evidence}”`);
    if (details.length) lines.push(`项目细节：${details.join("；")}`);
    lines.push(`品牌/型号指向：${features.targeted ? "命中 BLUESUN 或 BSM 型号" : "未命中"}`);
    const excerpt = shortEvidence(features.message_excerpt || inquiry.body, 360);
    if (excerpt) lines.push(`邮件证据摘录：${excerpt}`);
  }
  return lines.filter(Boolean);
}

const CONTACT_STATUS_LABELS = {
  PENDING: "待核验",
  MATCH: "公开证据匹配",
  NO_MATCH: "已检索未匹配",
  ERROR: "核验异常",
};
const MATCHED_BY_LABELS = {
  exact_email: "邮箱原文出现",
  official_domain: "企业官网域名",
  phone_and_identity: "电话与身份同页出现",
};
const COVERAGE_STATUS = {
  verified: { label: "核验通过", tone: "success" },
  body_fetched: { label: "取得正文 · 待核验", tone: "info" },
  found: { label: "找到来源 · 无正文", tone: "warning" },
  searched: { label: "已搜索 · 未找到来源", tone: "neutral" },
  completed: { label: "历史状态 · 待复核", tone: "neutral" },
  not_found: { label: "未检索到证据", tone: "warning" },
  failed: { label: "本次核验异常", tone: "danger" },
  manual_required: { label: "需授权或人工查询", tone: "info" },
  pending: { label: "待执行", tone: "neutral" },
  bonus_hit: { label: "加分", tone: "success" },
  no_bonus: { label: "未命中 · 不扣分", tone: "neutral" },
  risk_clear: { label: "未发现红旗", tone: "success" },
  risk_review: { label: "无红旗 · 有待核实", tone: "warning" },
  risk_hit: { label: "发现红旗", tone: "danger" },
};
const RISK_CHECK_TONE = { clear: "success", review: "warning", red: "danger" };
const KEY_FACT_TONE = { A: "success", B: "info", "": "warning" };

export function RatingDetailModal({ open, inquiry, selectedRating, loading, onClose, onSave, onRestore }) {
  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) return;
    setRating(Number(selectedRating || inquiry?.manual_business_value_rating || inquiry?.business_value_rating || 0));
    setReason("");
  }, [open, selectedRating, inquiry?.id, inquiry?.manual_business_value_rating, inquiry?.business_value_rating]);
  if (!inquiry) return null;
  const snapshot = inquiry.rating_snapshot || {};
  const breakdown = snapshot.breakdown || {};
  const hasManual = inquiry.manual_business_value_rating != null;
  const systemRating = Number(inquiry.system_business_value_rating || snapshot.system_rating || inquiry.business_value_rating || 0);
  const totalScore = Number(inquiry.rating_total_score || snapshot.total_score || 0);
  const verificationEvidence = snapshot.verification_evidence || {};
  const compatibility = inquiry.sop_compatibility || snapshot.sop_compatibility || {};
  const coverage = compatibility.research_coverage || { completed: 0, total: 7, steps: [] };
  const sevenStep = coverage.version === SEVEN_STEP_VERSION;
  // Only a stored pre-v2 snapshot is "legacy"; no coverage yet means not researched.
  const legacyCoverage = !sevenStep && Boolean(compatibility.research_coverage?.steps?.length);
  const emailMatches = verificationEvidence.email_matches || [];
  const matchedContactSources = [
    ...emailMatches.map((item) => ({ ...item, kind: "邮箱" })),
    ...(verificationEvidence.phone_matches || []).map((item) => ({ ...item, kind: "电话" })),
  ];
  const emailMatchLabel = inquiry.email_verification_status === "MATCH"
    && emailMatches.length
    && emailMatches.every((item) => item.matched_by === "official_domain")
    ? "企业域名匹配"
    : CONTACT_STATUS_LABELS[inquiry.email_verification_status] || "待核验";
  return (
    <Modal
      open={open}
      onClose={onClose}
      width="xl"
      title="客户星级与评分依据"
      description={`规则 ${inquiry.rating_rule_version || snapshot.rule_version || "bluesun_47_v2"} · 同一客户项目往来合并计算`}
      footer={(
        <>
          {hasManual ? <Button icon={RotateCcw} onClick={onRestore} disabled={loading}>恢复系统评分</Button> : null}
          <Button onClick={onClose}>取消</Button>
          <Button variant="primary" icon={Save} loading={loading} disabled={!rating || !reason.trim()} onClick={() => onSave(rating, reason.trim())}>保存人工调整</Button>
        </>
      )}
    >
      <div className="rating-detail">
        <section className="rating-sop-strip" aria-label="评级与背调摘要">
          <span>
            <small>当前有效评级</small>
            <Stars value={inquiry.business_value_rating} />
            <Badge tone={hasManual ? "warning" : "success"}>{hasManual ? `已人工调整 · 系统 ${systemRating} 星` : "系统评分生效"}</Badge>
          </span>
          <span><small>规则得分</small><strong>{totalScore} / 47</strong></span>
          <span><small>身份可信</small><strong>{VERIFICATION_LEVEL_LABELS[inquiry.verification_level] || "待核验"}</strong>{inquiry.price_protection ? <Badge tone="warning">报价保护开启</Badge> : null}</span>
          <span><small>SOP 分层</small><strong>SOP-{inquiry.sop_segment || compatibility.segment || "U"}</strong></span>
          <span><small>背调优先级</small><strong>{inquiry.research_priority || compatibility.research_priority || "P3"}</strong></span>
        </section>
        {(snapshot.review_flags || []).length || (snapshot.risk_flags || []).length || (snapshot.next_verification_questions || []).length ? (
          <section className="rating-detail__signals">
            <strong>核验与跟进</strong>
            {(snapshot.review_flags || []).map((item) => <Badge key={item} tone="warning"><span title={item}>{reviewFlagLabel(item)}</span></Badge>)}
            {(snapshot.risk_flags || []).map((item) => <Badge key={item} tone={item.startsWith("REVIEW_") ? "warning" : "danger"}><span title={item}>{riskFlagLabel(item)}</span></Badge>)}
            {(snapshot.next_verification_questions || []).map((item) => <p key={item}>· {item}</p>)}
          </section>
        ) : null}
        <section className="rating-contact-verification">
          <header><strong>联系方式与证据闭环</strong><small>未匹配或核验异常只产生提醒，不阻断自动回复</small></header>
          <div>
            <span><small>邮箱</small><Badge tone={inquiry.email_verification_status === "MATCH" ? "success" : inquiry.email_verification_status === "ERROR" ? "danger" : "warning"}>{emailMatchLabel}</Badge></span>
            <span><small>电话 / WhatsApp</small><Badge tone={inquiry.phone_verification_status === "MATCH" ? "success" : inquiry.phone_verification_status === "ERROR" ? "danger" : "warning"}>{CONTACT_STATUS_LABELS[inquiry.phone_verification_status] || "待核验"}</Badge></span>
            <span><small>独立来源</small><strong>{Number(verificationEvidence.independent_source_count || 0)} 个</strong></span>
            <span><small>结构性硬证据</small><strong>{verificationEvidence.hard_evidence_present ? "已具备" : "尚未形成"}</strong></span>
          </div>
          {matchedContactSources.length ? (
            <ul className="rating-contact-verification__sources" aria-label="联系方式匹配来源">
              {matchedContactSources.map((item) => (
                <li key={`${item.kind}-${item.matched_by}-${item.url}`}>
                  <Badge tone="neutral">{item.kind} · {MATCHED_BY_LABELS[item.matched_by] || item.matched_by}</Badge>
                  {item.url ? <a href={item.url} target="_blank" rel="noreferrer noopener">{item.source_name || item.domain || item.url}</a> : <span>{item.source_name || item.domain}</span>}
                </li>
              ))}
            </ul>
          ) : null}
          <small>域名匹配只证明企业官网关系，不等于联系人身份已确认；域名、注册与制裁风险仅依据已抓取的公开证据。</small>
        </section>
        <details className="sop-coverage-details">
          <summary>
            <span>
              <strong>{sevenStep ? "七步背调（知识库 SOP）" : legacyCoverage ? "六步背调覆盖（旧版，重新背调后更新为七步）" : "七步背调（知识库 SOP · 尚未背调）"}</strong>
              <small>“未检索到证据”不等于客户或企业不存在{sevenStep ? "；第 6 步只加分不扣分" : ""}</small>
            </span>
            <b>{Number(coverage.completed || 0)} / {Number(coverage.total || 7)} {sevenStep ? "已完成" : "核验通过"}</b>
          </summary>
          <section className="sop-coverage">
          <i aria-hidden="true"><b style={{ width: `${Math.min(100, Number(coverage.completed || 0) * 100 / Math.max(1, Number(coverage.total || 7)))}%` }} /></i>
          <div className="research-stage-summary" aria-label="背调信息获取阶段">
            <span>已搜索 <b>{coverage.searched || 0}</b></span>
            <span>找到来源 <b>{coverage.found || 0}</b></span>
            <span>取得正文 <b>{coverage.body_fetched || 0}</b></span>
            <span>核验通过 <b>{coverage.verified || 0}</b></span>
          </div>
          <div className="sop-coverage__steps">
            {(coverage.steps || []).map((step, index) => {
              const state = COVERAGE_STATUS[step.status] || COVERAGE_STATUS.pending;
              return (
                <article key={step.key || index} className={`is-${step.status || "pending"}${step.kind ? ` is-${step.kind}` : ""}`}>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{step.label}</strong>
                    {step.kind === "risk" ? null : <small>{Number(step.source_count || 0)} 个来源 · {Number(step.body_count || 0)} 个正文</small>}
                    <small>{step.reason}</small>
                    {step.checks?.length ? (
                      <span className="sop-coverage__checks">
                        {step.checks.map((check) => <Badge key={check.key} tone={RISK_CHECK_TONE[check.state] || "neutral"}><span title={(check.flags || []).join(", ")}>{check.label}</span></Badge>)}
                      </span>
                    ) : null}
                  </div>
                  <Badge tone={state.tone}>{state.label}</Badge>
                </article>
              );
            })}
          </div>
          {coverage.key_facts?.length ? (
            <div className="sop-key-facts" aria-label="多源交叉验证">
              <strong>多源交叉验证<small>A 级：四类关键事实各 ≥3 个独立来源；B 级：≥2 个</small></strong>
              {coverage.key_facts.map((fact) => (
                <span key={fact.key}>
                  <b>{fact.label}</b>
                  <Badge tone={KEY_FACT_TONE[fact.meets || ""]}>{fact.independent_source_count} 源{fact.meets ? ` · 满足 ${fact.meets}` : " · 不足"}</Badge>
                  <small>{(fact.sources || []).join(" / ") || "暂无已取得正文的来源"}</small>
                </span>
              ))}
            </div>
          ) : null}
          <p>{compatibility.segment_reason || inquiry.sop_segment_reason || "SOP 分层由现有评分和公开证据自动映射，不单独改变五星评分。"}</p>
          {compatibility.assessment ? <div>
            <p>企业类型：{compatibility.assessment.subtype || "待核实"} · 第三方硬来源：{compatibility.assessment.hard_source_count || 0} 个</p>
            {compatibility.assessment.historical_protected ? <p>已保留历史 B 级，新的证据缺口已列入复核。</p> : null}
            <p>待补证据：{(compatibility.assessment.missing_evidence || []).join("；") || "评级依据齐全"}</p>
            <small>企业等级独立于询盘星级；富通原始数据完成核验后再参与评级。</small>
          </div> : null}
          </section>
        </details>
        <section className="rating-breakdown">
          <header><ShieldCheck size={17} /><div><strong>47分评分明细</strong><small>五项得分相加后换算星级：{RATING_STAR_THRESHOLDS.map(([star, range]) => `${star} ${range}`).join(" · ")}</small></div></header>
          {Object.entries(RATING_DIMENSIONS).map(([key, dimension]) => {
            const item = breakdown[key] || {};
            const maximum = Number(item.maximum || 0);
            const score = Number(item.score || 0);
            const rules = snapshot.rating_rules?.[key] || dimension.rules;
            const evidence = ratingEvidenceLines(key, item, inquiry);
            return (
              <article key={key}>
                <header className="rating-breakdown__result">
                  <div><strong>{dimension.label}</strong><span>{score} / {maximum}</span></div>
                  <i aria-hidden="true"><b style={{ width: `${maximum ? (score / maximum) * 100 : 0}%` }} /></i>
                  <p><b>本项命中：</b>{item.reason || "尚未计算"}</p>
                </header>
                <details>
                  <summary>查看评分标准与本次依据</summary>
                  <div className="rating-breakdown__detail">
                  <section className="rating-breakdown__rules">
                    <strong>评分标准</strong>
                    <ul>{rules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
                  </section>
                  <section className="rating-breakdown__evidence">
                    <strong>本次询盘具体依据</strong>
                    <ul>{evidence.length ? evidence.map((line) => <li key={line}>{line}</li>) : <li>暂未记录可展示的依据</li>}</ul>
                  </section>
                  </div>
                </details>
              </article>
            );
          })}
        </section>
        <section className="rating-manual-edit">
          <div><strong>人工调整</strong><small>人工星级不会覆盖系统计算结果，可以随时恢复。</small></div>
          <Stars value={rating} editable onChange={setRating} label="人工评级" />
          <TextArea rows={3} value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="请填写调整依据，例如：客户确认项目延期、采购规模已核实。" />
        </section>
        {(inquiry.rating_history || []).length ? <details className="rating-history"><summary>查看最近评分记录</summary><div>{inquiry.rating_history.slice(0, 8).map((item) => <article key={item.id}><span><strong>{item.effective_rating} 星</strong><small>{item.total_score} / 47 · {item.stage}</small></span><p>{item.reason || "系统自动重算"}</p><time>{formatFullDateTime(item.created_at)}</time></article>)}</div></details> : null}
      </div>
    </Modal>
  );
}

export function OriginalEmailModal({ inquiry, open, onClose, notify, onSaved }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true); setError("");
    try {
      const result = await api.originalEmail(inquiry.id);
      setData(result);
      setBody(result.extracted_body || inquiry.body || result.corrected_body_full_text || result.body_text || result.raw_excerpt || "");
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && inquiry?.id) load(); }, [open, inquiry?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      const updated = await api.saveInquiryBody(inquiry.id, body);
      onSaved(updated); notify("客户原文已保存", "success"); onClose();
    } catch (err) { notify("保存失败", "danger", err.message); }
  };
  return (
    <Modal open={open} onClose={onClose} width="xl" title="原始邮件与客户原文校正" description={inquiry.subject} footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" icon={Save} disabled={!body.trim()} onClick={save}>保存为客户原文</Button></>}>
      {loading ? <LoadingState label="正在从数据库读取原始邮件…" /> : error ? <ErrorState message={error} onRetry={load} /> : (
        <>
          <div className="raw-email-meta">
            <span><small>发件人</small>{data?.from || inquiry.sender_email || "—"}</span>
            <span><small>收件人</small>{data?.to || inquiry.recipient_email || "—"}</span>
            <span><small>日期</small>{data?.date || formatFullDateTime(inquiry.received_at)}</span>
          </div>
          <div className="raw-email-columns">
            <section><header><strong>本次客户正文</strong><small>可校正；保存后仅用于后续 AI 回复和背调</small></header><TextArea value={body} onChange={(event) => setBody(event.target.value)} /></section>
            <section>
              <header><strong>完整往来记录</strong><small>由每封独立客户原文和已发送回复按时间还原</small></header>
              {data?.conversation_messages?.length ? (
                <div className="raw-conversation">
                  {data.conversation_messages.map((message, index) => (
                    <article key={`${message.kind}-${message.inquiry_id}-${message.date}-${index}`} className={`raw-conversation__message is-${message.kind}`}>
                      <header>
                        <span><strong>{message.kind === "customer" ? "客户来信" : "BLUESUN 已发送回复"}</strong>{message.is_current ? <em>当前邮件</em> : null}</span>
                        <time>{formatFullDateTime(message.date)}</time>
                      </header>
                      <small>{safeText(message.name)}{message.email ? ` <${message.email}>` : ""}</small>
                      <b>{safeText(message.subject, "无主题")}</b>
                      <pre>{message.body_text}</pre>
                    </article>
                  ))}
                </div>
              ) : data?.body_html ? <div className="email-html" dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(data.body_html) }} /> : <pre>{data?.body_text || data?.raw_excerpt || data?.raw_unavailable_reason || "暂未读取到原始正文。"}</pre>}
            </section>
          </div>
        </>
      )}
    </Modal>
  );
}

export function ReplyPreviewModal({ open, onClose, inquiry, settings, payload }) {
  if (!payload) return null;
  const signatureHtml = settings?.reply_signature_enabled ? String(settings?.reply_signature_html || "") : "";
  const signatureText = settings?.reply_signature_enabled ? String(settings?.reply_signature || "") : "";
  const subject = /^re:/i.test(inquiry.subject || "") ? inquiry.subject : `Re: ${inquiry.subject || "Inquiry"}`;
  return (
    <Modal open={open} onClose={onClose} width="lg" title="发送预览" description="以下内容与收件方看到的邮件结构一致" footer={<Button variant="primary" onClick={onClose}>关闭预览</Button>}>
      <article className="mail-preview">
        <h2>{subject}</h2>
        <dl>
          <div><dt>发件人</dt><dd>{settings?.display_name || "BLUESUN Sales"} &lt;{settings?.email_address || "—"}&gt;</dd></div>
          <div><dt>收件人</dt><dd>{payload.recipients.join(", ")}</dd></div>
          {payload.cc.length ? <div><dt>抄送</dt><dd>{payload.cc.join(", ")}</dd></div> : null}
        </dl>
        <div className="mail-preview__body"><pre>{payload.draft}</pre></div>
        {signatureHtml ? <div className="mail-preview__signature" dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(signatureHtml) }} /> : signatureText ? <pre className="mail-preview__signature">{signatureText}</pre> : null}
        <blockquote>
          <small>On {formatFullDateTime(inquiry.received_at)}, {safeText(inquiry.sender_name || inquiry.sender_email)} wrote:</small>
          <pre>{inquiry.body || "原邮件正文为空。"}</pre>
        </blockquote>
      </article>
    </Modal>
  );
}

export function SendConfirm({ open, onClose, inquiry, payload, onConfirm, loading }) {
  const warningCount = Array.isArray(inquiry?.reply_validation_warnings) ? inquiry.reply_validation_warnings.length : 0;
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      loading={loading}
      title="确认发送邮件"
      description={`将通过当前业务邮箱回复 ${payload?.recipients?.join(", ") || inquiry?.sender_email || "客户"}。${warningCount ? `当前草稿有 ${warningCount} 项审核提醒，提交即表示已人工核对。` : ""}提交后可继续处理其他询盘，系统将在后台完成发送。`}
      confirmLabel={warningCount ? "确认已审核并发送" : "提交后台发送"}
    />
  );
}

export function PromptTemplatesModal({ open, onClose, notify }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({ name: "", prompt: "" });
  const [busy, setBusy] = useState("");
  const current = useMemo(() => items.find((item) => String(item.id) === String(selected)), [items, selected]);
  const load = async () => {
    try {
      const result = await api.researchPrompts();
      const next = result.items || result || [];
      setItems(next);
      if (!selected && next[0]) setSelected(String(next.find((item) => item.is_active)?.id || next[0].id));
    } catch (err) { notify("模板加载失败", "danger", err.message); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (current) setForm({ name: current.name, prompt: current.prompt }); }, [current]);
  const run = async (key, action) => {
    setBusy(key);
    try { await action(); await load(); notify("背调模板已更新", "success"); } catch (err) { notify("操作失败", "danger", err.message); } finally { setBusy(""); }
  };
  const isNew = selected === "new";
  return (
    <Modal open={open} onClose={onClose} width="lg" title="AI 背调提示词模板" description="最多保存 5 个模板；启用模板用于后续背调。">
      <div className="prompt-manager">
        <aside>
          {items.map((item) => <button key={item.id} className={String(item.id) === selected ? "is-active" : ""} onClick={() => setSelected(String(item.id))}><span>{item.name}</span>{item.is_active ? <Check size={14} /> : null}</button>)}
          {items.length < 5 ? <button onClick={() => { setSelected("new"); setForm({ name: "", prompt: "" }); }}><Plus size={14} />新建模板</button> : null}
        </aside>
        <section>
          <Field label="模板名称" required><TextInput value={form.name} maxLength={60} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
          <Field label="提示词" required hint={`${form.prompt.length} / 12000`}><TextArea value={form.prompt} minLength={30} maxLength={12000} onChange={(event) => setForm({ ...form, prompt: event.target.value })} /></Field>
          <div className="prompt-actions">
            {!isNew && current ? <Button icon={Check} loading={busy === "active"} onClick={() => run("active", () => api.activateResearchPrompt(current.id))}>设为启用</Button> : null}
            <Button variant="primary" icon={Save} loading={busy === "save"} disabled={!form.name.trim() || form.prompt.trim().length < 30} onClick={() => run("save", () => isNew ? api.createResearchPrompt(form) : api.updateResearchPrompt(current.id, form))}>{isNew ? "创建模板" : "保存修改"}</Button>
            {!isNew && current ? <Button variant="danger" icon={Trash2} loading={busy === "delete"} onClick={() => run("delete", () => api.deleteResearchPrompt(current.id))}>删除</Button> : null}
            <Button variant="ghost" icon={RotateCcw} loading={busy === "restore"} onClick={() => run("restore", api.restoreDefaultResearchPrompt)}>恢复默认</Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
